import { REQ_STATUS } from 'packages/at-shell/utils/types';
import { atom, atomFamily, GetRecoilValue, selector } from 'recoil';
import { userScopeIncludesOneOf, userEmail } from 'packages/lib/auth';
import { taskAtom, toolAliasAtom } from 'packages/at-shell/state/recoil/task';
import {
  multiTasksAtom,
  tasksByObjectTokenAtom,
} from 'packages/at-shell/state/recoil/multitask-workflow';
import { ReactNode } from 'react';
import { doesToolRequireLinkedTasks } from 'packages/at-shell/utils/use-does-tool-require-linked-tasks';
import { localStorageEffect } from 'packages/at-shell/state/recoil/effects/local-storage-effect';
import { Task } from 'packages/lib/api/annotations/contract/task';
import { getCurrentUserSafely } from 'packages/roster-man/api/use-users';
import { getUserToolRole, PermissionRole } from 'packages/lib/auth/permissions';
import { isActiveUser } from 'packages/lib/auth/utils';

export const showSessionTimerState = atom({
  key: 'show-session-timer-state',
  default: false,
});

export const saveToApiStatusAtom = atom<REQ_STATUS>({
  key: 'saveToApiStatusAtom',
  default: REQ_STATUS.success,
});

function isTaskInProgress(task: Task | null | undefined) {
  const annotationState = task?.annotationState ?? '';
  return (
    annotationState.includes('InProgress') ||
    annotationState.includes('InReview')
  );
}

const workforceUserSelector = selector({
  key: 'user-permission-selector',
  get: async () => {
    const user = await getCurrentUserSafely();
    if (!user) {
      return null;
    }
    return { ...user, permissions: user.permissions ?? '' };
  },
});

const usingWorkforcePermissionSelector = selector({
  key: 'using-workforce-permission-selector',
  get: async () => {
    const { search } = window.location;
    const params = new URLSearchParams(search);
    const usingWorkforce = params.get('workforce-perms');
    return usingWorkforce === 'true';
  },
});

export const defaultEditAccessSelector = selector({
  key: 'has-edit-access-default-value-selector',
  get: async ({ get }) => {
    const userHasEditScopes = await userScopeIncludesOneOf([
      'write:annotations:lidar',
      'write:annotations',
    ]);
    const toolIsInEditMode = get(personaAtom) !== 'view';
    const user = await userEmail();
    const task = get(taskAtom);
    const taskIsAssignedToUser = user && task ? user === task.assignee : false;
    const workforceUser = get(workforceUserSelector);
    const toolAlias = get(toolAliasAtom);
    const userHasPermission =
      workforceUser && toolAlias
        ? getUserToolRole(workforceUser, toolAlias) !== PermissionRole.View
        : false;
    const userIsActive = isActiveUser(workforceUser);
    const usingWorkforcePerms = get(usingWorkforcePermissionSelector);

    return (
      (usingWorkforcePerms
        ? userHasPermission && userIsActive
        : userHasEditScopes) &&
      userHasEditScopes &&
      toolIsInEditMode &&
      taskIsAssignedToUser &&
      isTaskInProgress(task)
    );
  },
});

export const hasEditAccessAtom = atom<boolean>({
  key: 'has-edit-access-atom',
  default: defaultEditAccessSelector,
});

export const hasMultiTaskEditAccessAtom = atom<boolean>({
  key: 'has-multi-task-edit-access-atom',
  default: selector({
    key: 'has-multi-task-edit-access-default-value-selector',
    get: async ({ get }) => {
      const isLinkedTaskWorkflow = doesToolRequireLinkedTasks(
        window.location.pathname
      );
      return isLinkedTaskWorkflow
        ? await hasLinkedTaskEditAccess(get)
        : await hasMultiTaskEditAccess(get);
    },
  }),
});

const hasMultiTaskEditAccess = async (get: GetRecoilValue) => {
  const userHasEditScopes = await userScopeIncludesOneOf([
    'write:annotations:lidar',
    'write:annotations',
  ]);
  const user = await userEmail();
  const tasks = get(multiTasksAtom);
  if (!tasks) {
    return false;
  }

  const allTasksAreAssignedToUser = tasks.every((task) =>
    user && task ? user === task.assignee : false
  );
  const allTasksInProgress = tasks.every((task) => isTaskInProgress(task));

  const workforceUser = get(workforceUserSelector);
  const toolAlias = get(toolAliasAtom);
  const userHasPermission =
    workforceUser && toolAlias
      ? getUserToolRole(workforceUser, toolAlias) !== PermissionRole.View
      : false;
  const usingWorkforcePerms = get(usingWorkforcePermissionSelector);
  const userIsActive = isActiveUser(workforceUser);

  return (
    (usingWorkforcePerms
      ? userHasPermission && userIsActive
      : userHasEditScopes) &&
    userHasEditScopes &&
    allTasksAreAssignedToUser &&
    allTasksInProgress
  );
};

const hasLinkedTaskEditAccess = async (get: GetRecoilValue) => {
  const userHasEditScopes = await userScopeIncludesOneOf([
    'write:annotations:lidar',
    'write:annotations',
  ]);
  const user = await userEmail();
  const tasks = get(multiTasksAtom);
  const tasksByObjectToken = get(tasksByObjectTokenAtom);
  if (!tasks || !tasksByObjectToken) {
    return false;
  }

  const allTasksAreAssignedToUser = tasksByObjectToken.every((task) =>
    user && task ? user === task.assignee : false
  );
  const allTasksInProgress = tasksByObjectToken.every((task) =>
    isTaskInProgress(task)
  );

  const workforceUser = get(workforceUserSelector);
  const toolAlias = get(toolAliasAtom);
  const userHasPermission =
    workforceUser && toolAlias
      ? getUserToolRole(workforceUser, toolAlias) !== PermissionRole.View
      : false;
  const usingWorkforcePerms = get(usingWorkforcePermissionSelector);
  const userIsActive = isActiveUser(workforceUser);

  return (
    (usingWorkforcePerms
      ? userHasPermission && userIsActive
      : userHasEditScopes) &&
    userHasEditScopes &&
    allTasksAreAssignedToUser &&
    allTasksInProgress
  );
};

export const devModeAtom = atom<boolean>({
  key: 'devModeAtom',
  default: false,
});
export const uiThemeAtom = atom<'light' | 'dark'>({
  key: 'uiThemeAtom',
  default: 'light',
  effects: [localStorageEffect('ui-theme')],
});
export const submitDialogAtomFam = atomFamily({
  key: 'submit-dialog-atom-family',
  default: false,
});
/** Used to prevent user from multiple submissions of the same task / set of tasks */
export const submitSuccessAtom = atom<boolean>({
  key: 'submit-success-atom',
  default: false,
});
/** Increment this to trigger a reload (revalidate) of the useAssignedTasks data. */
export const multitaskBatchIDAtom = atom<number>({
  key: 'multitask-batch-id-atom',
  default: 0,
});
export const personaAtom = atom<string>({
  key: 'personaAtom',
  default: 'label',
});
export const toolbarPopoverStateAtomFam = atomFamily<boolean, string>({
  key: 'toolbar-popover-state-atom-fam',
  default: false,
});
export const instructionsPopoverStateAtomFam = atomFamily<boolean, string>({
  key: 'instructions-popover-state-atom-fam',
  default: false,
});
export const hudPillAtom = atom({
  key: 'hud-pill-atom',
  default: '',
});
export const toolDialogStateAtomFam = atomFamily<boolean, string>({
  key: 'tool-dialog-state-atom-fam',
  default: true,
});
/** Set to true to prevent the Task Pickup prompt from appearing.
 * Set to false to allow it to appear when currentUserCanPickupTask() is true
 */
export const forceClosedTaskPickupPrompt = atom({
  key: 'force-closed-task-pickup-prompt',
  default: false,
});
export const hotkeysModalState = atom({
  key: 'hotkeys-menu-state-atom',
  default: false,
});
/** Optional text that can be added to at-shell title bar, just after breadcrumbs */
export const atShellCustomTitleTextAtom = atom<ReactNode>({
  key: 'at-shell-custom-title-text-atom',
  default: null,
});
export const annotationHistoryDialogIsOpenAtom = atom<boolean>({
  key: 'at-shell-annotation-history-dialog-is-open-atom',
  default: false,
});
export const isToolbarCollapsedAtom = atom<boolean>({
  key: 'at-shell-is-toolbar-collapsed-atom',
  default: false,
});
